/**
 * Firebase Configuration for Feline-SA
 * 
 * SETUP INSTRUCTIONS:
 * 1. Go to https://console.firebase.google.com
 * 2. Create a new project named "feline-sa" (or similar)
 * 3. Enable Authentication → Email/Password
 * 4. Enable Firestore Database (Start in test mode for development)
 * 5. Enable Storage (for cat photos)
 * 6. Enable Hosting
 * 7. Go to Project Settings → General → Your apps → Add app → Web
 * 8. Copy your config values below
 */

import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

// Replace these with your actual Firebase config
const firebaseConfig = {
  apiKey: "AIzaSyDr5kM2iOuLpamkqFd7ZjG1ODaV9O90KqY",
  authDomain: "thepedigreeapp.firebaseapp.com",
  projectId: "thepedigreeapp",
  storageBucket: "thepedigreeapp.firebasestorage.app",
  messagingSenderId: "856952856919",
  appId: "1:856952856919:web:cfd39131acd207a7e8de12"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

export default app;

/**
 * FIRESTORE COLLECTIONS STRUCTURE:
 * 
 * catteries/
 *   {catteryId}/
 *     name: string
 *     breederId: string (ref to users)
 *     association: "SACC" | "CASA" | "CFSA"
 *     registrationNumber: string
 *     location: { province: string, city: string }
 *     breeds: string[]
 *     established: timestamp
 *     verified: boolean
 *     createdAt: timestamp
 *     updatedAt: timestamp
 * 
 * users/
 *   {userId}/
 *     email: string
 *     displayName: string
 *     role: "breeder" | "buyer" | "admin"
 *     catteryId: string (if breeder)
 *     phone: string
 *     createdAt: timestamp
 * 
 * cats/
 *   {catId}/
 *     name: string
 *     microchip: string
 *     registrationNumber: string
 *     ems: string (EMS code e.g., "MCO n 22")
 *     breed: string
 *     color: string
 *     sex: "male" | "female"
 *     dateOfBirth: timestamp
 *     sireId: string (ref to cats)
 *     damId: string (ref to cats)
 *     catteryId: string (ref to catteries)
 *     status: "available" | "reserved" | "sold" | "not_for_sale"
 *     price: number (in ZAR)
 *     photos: string[]
 *     health: {
 *       vaccinations: array
 *       tests: array
 *       neutered: boolean
 *     }
 *     // Fixed Asset fields (for breeding stock)
 *     assetValue: number (purchase price or estimated value)
 *     assetCategory: "stud" | "queen" | "retired" | "kitten_hold"
 *     litterCount: number (total litters produced)
 *     offspringCount: number (total kittens produced)
 *     revenueGenerated: number (total sales from offspring)
 *     titles: string[] (CH, GC, etc.)
 *     assetNotes: string
 *     createdAt: timestamp
 *     updatedAt: timestamp
 * 
 * litters/
 *   {litterId}/
 *     sireId: string
 *     damId: string
 *     catteryId: string
 *     dateOfBirth: timestamp
 *     kittenIds: string[]
 *     createdAt: timestamp
 * 
 * registrations/
 *   {registrationId}/
 *     catId: string
 *     catteryId: string
 *     association: string
 *     status: "pending" | "submitted" | "approved" | "rejected"
 *     fee: number
 *     serviceFee: number (10% commission)
 *     pdfUrl: string
 *     submittedAt: timestamp
 *     approvedAt: timestamp
 * 
 * commission_ledger/
 *   {entryId}/
 *     breederId: string
 *     catteryId: string
 *     type: "sale" | "registration"
 *     saleId: string | null
 *     registrationId: string | null
 *     saleAmount: number
 *     commissionAmount: number
 *     commissionRate: number (0.10)
 *     status: "pending" | "paid"
 *     month: string (e.g., "Jan-2026")
 *     dueDate: timestamp
 *     paidAt: timestamp | null
 *     createdAt: timestamp
 * 
 * sales/
 *   {saleId}/
 *     catId: string
 *     catteryId: string
 *     buyerId: string
 *     price: number
 *     status: "reserved" | "completed" | "cancelled"
 *     reservedAt: timestamp
 *     completedAt: timestamp
 *     commissionEntryId: string
 * 
 * enquiries/
 *   {enquiryId}/
 *     catId: string
 *     catteryId: string
 *     buyerName: string
 *     buyerEmail: string
 *     buyerPhone: string
 *     message: string
 *     status: "new" | "contacted" | "converted" | "closed"
 *     createdAt: timestamp
 */
